import { selectAuth } from '@brands/store/selectors/auth';
import uniqBy from 'lodash/uniqBy';
import { useCallback, useEffect, useRef, useState } from 'react';

import { listAvailableProviders, ListProvidersParams, ProviderAvailability } from '../services/availability/listAvailableProviders';
import { Meta } from '../services/communication/types/Meta';
import { useAppSelector } from './useReduxHook';

export default function useProviders(params: ListProvidersParams) {
  const { userInfo } = useAppSelector(selectAuth);
  const [providers, setProviders] = useState<ProviderAvailability[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [firstPageMeta, setFirstPageMeta] = useState<Meta | null>(null);
  const [lastPageMeta, setLastPageMeta] = useState<Meta | null>(null);
  const requestVersion = useRef(0);
  const pagePending = useRef(false);

  const reloadAll = useCallback(async (): Promise<void> => {
    const version = ++requestVersion.current;
    setDataLoading(true);
    setLoadError(null);
    setProviders([]);
    setFirstPageMeta(null);
    setLastPageMeta(null);
    try {
      const result = await listAvailableProviders(params, userInfo.role.name);
      if (version !== requestVersion.current) return;
      setProviders(result.nodes);
      setFirstPageMeta(result.meta);
      setLastPageMeta(result.meta);
    } catch {
      if (version === requestVersion.current) setLoadError('Unable to load providers. Please try again.');
    } finally {
      if (version === requestVersion.current) setDataLoading(false);
    }
  }, [params, userInfo.id, userInfo.role.name]);

  const loadPage = async (direction: 'next' | 'previous'): Promise<void> => {
    const meta = direction === 'next' ? lastPageMeta : firstPageMeta;
    if (dataLoading || pagePending.current || !meta || !(direction === 'next' ? meta.has_next_page : meta.has_previous_page)) return;
    const version = requestVersion.current;
    pagePending.current = true;
    try {
      const result = await listAvailableProviders({
        ...params,
        cursor: direction === 'next' ? meta.end_cursor : meta.start_cursor,
        cursor_direction: direction,
      }, userInfo.role.name);
      if (version !== requestVersion.current) return;
      setLoadError(null);
      if (direction === 'next') {
        setLastPageMeta(result.meta);
        setProviders(previous => uniqBy([...previous, ...result.nodes], 'id'));
      } else {
        setFirstPageMeta(result.meta);
        setProviders(previous => uniqBy([...result.nodes, ...previous], 'id'));
      }
    } catch {
      if (version === requestVersion.current) setLoadError('Unable to load providers. Please try again.');
    } finally {
      pagePending.current = false;
    }
  };

  useEffect(() => {
    void reloadAll();
    return () => { requestVersion.current += 1; };
  }, [reloadAll]);

  return { providers, dataLoading, loadError, reloadAll,
    loadNextPage: () => loadPage('next'), loadPreviousPage: () => loadPage('previous') };
}
